import type { Recipe, RecipeCategory } from "@/types/recipe"
import { getAllRecipes, getRecipeBySlug } from "@/data/recipes"

const recipeCategoryLabels: Record<RecipeCategory, string> = {
  cucumbers: "Огірки",
  tomatoes: "Помідори",
  zucchini: "Кабачки",
  peppers: "Перець",
  eggplants: "Баклажани",
  berries: "Ягоди",
  jam: "Варення",
  compote: "Компоти",
  fermentation: "Ферментація",
  other: "Інше",
}

const seasonLabels: Record<string, string> = {
  spring: "Весна",
  summer: "Літо",
  autumn: "Осінь",
  winter: "Зима",
}

function parseRecipeDate(value: string | undefined) {
  if (!value) {
    return 0
  }

  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? 0 : parsed
}

function getSharedCount(left: string[], right: string[]) {
  const rightSet = new Set(right)
  return left.filter((item) => rightSet.has(item)).length
}

function scoreRelatedRecipe(currentRecipe: Recipe, candidate: Recipe) {
  let score = 0

  if (candidate.category === currentRecipe.category) {
    score += 5
  }

  score += getSharedCount(candidate.tags, currentRecipe.tags) * 3
  score += getSharedCount(candidate.season, currentRecipe.season) * 2

  if (candidate.difficulty === currentRecipe.difficulty) {
    score += 1
  }

  if (candidate.featured) {
    score += 0.5
  }

  return score
}

export function getAllRecipeSlugs() {
  return getAllRecipes().map((recipe) => recipe.slug)
}

export function getRecipeOrNull(slug: string) {
  return getRecipeBySlug(slug) ?? null
}

export function getRelatedRecipes(currentRecipe: Recipe, limit = 3) {
  const allRecipes = getAllRecipes()

  return allRecipes
    .filter((recipe) => recipe.slug !== currentRecipe.slug)
    .map((recipe) => ({
      recipe,
      score: scoreRelatedRecipe(currentRecipe, recipe),
      updatedAtScore: parseRecipeDate(recipe.updatedAt ?? recipe.publishedAt),
    }))
    .sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score
      }

      if (b.updatedAtScore !== a.updatedAtScore) {
        return b.updatedAtScore - a.updatedAtScore
      }

      return a.recipe.title.localeCompare(b.recipe.title, "uk")
    })
    .slice(0, limit)
    .map((item) => item.recipe)
}

export function getRecipeBreadcrumbs(recipe: Recipe) {
  return [
    { title: "Головна", href: "/" },
    { title: "Рецепти", href: "/recipes" },
    { title: recipe.title, href: `/recipes/${recipe.slug}` },
  ]
}

export function getRecipeCategoryLabel(category: RecipeCategory) {
  return recipeCategoryLabels[category]
}

export function getRecipeSeasonLabels(recipe: Recipe) {
  return recipe.season.map((season) => seasonLabels[season] ?? season)
}

export function getRecipesByCategory(category: RecipeCategory) {
  return getAllRecipes().filter((recipe) => recipe.category === category)
}

export function getFeaturedRecipesByCategory(category: RecipeCategory, limit = 3) {
  return getAllRecipes()
    .filter((recipe) => recipe.category === category && recipe.featured)
    .slice(0, limit)
}

export function getRecipesBySeason(season: string) {
  return getAllRecipes().filter((recipe) => recipe.season.includes(season))
}